/**
 * Bot Profiles Data
 *
 * Rich metadata for all supported AI bots including company info,
 * descriptions, categories, and visual styling.
 */

export interface BotProfile {
  identifier: string;
  name: string;
  company: string;
  category: 'training' | 'assistant' | 'search';
  description: string;
  shortDescription: string;
  respectsRobotsTxt: boolean;
  color: string;
  logoUrl?: string;
}

export const BOT_CATEGORIES = {
  training: {
    label: 'Training',
    description: 'Crawls content to train AI models',
    color: 'var(--bot-training, #8B5CF6)',
    bgColor: 'bg-purple-500/10',
    textColor: 'text-purple-600 dark:text-purple-400',
    borderColor: 'border-purple-500/30',
  },
  assistant: {
    label: 'Assistant',
    description: 'Browses on behalf of users in real-time',
    color: 'var(--bot-assistant, #3B82F6)',
    bgColor: 'bg-blue-500/10',
    textColor: 'text-blue-600 dark:text-blue-400',
    borderColor: 'border-blue-500/30',
  },
  search: {
    label: 'Search',
    description: 'AI-powered search engine crawler',
    color: 'var(--bot-search, #10B981)',
    bgColor: 'bg-green-500/10',
    textColor: 'text-green-600 dark:text-green-400',
    borderColor: 'border-green-500/30',
  },
} as const;

export const BOT_PROFILES: Record<string, BotProfile> = {
  // OpenAI
  GPTBot: {
    identifier: 'GPTBot',
    name: 'GPTBot',
    company: 'OpenAI',
    category: 'training',
    description: 'GPTBot crawls content to train ChatGPT and other OpenAI models. Your content may appear in AI-generated responses across millions of conversations.',
    shortDescription: 'Trains ChatGPT models',
    respectsRobotsTxt: true,
    color: '#10A37F',
  },
  'ChatGPT-User': {
    identifier: 'ChatGPT-User',
    name: 'ChatGPT-User',
    company: 'OpenAI',
    category: 'assistant',
    description: 'ChatGPT-User browses the web on behalf of ChatGPT Plus users when they ask questions that require current information.',
    shortDescription: 'Real-time browsing for ChatGPT',
    respectsRobotsTxt: true,
    color: '#10A37F',
  },
  'OAI-SearchBot': {
    identifier: 'OAI-SearchBot',
    name: 'OAI-SearchBot',
    company: 'OpenAI',
    category: 'search',
    description: 'OAI-SearchBot powers OpenAI\'s AI-powered search capabilities, fetching real-time information for search queries.',
    shortDescription: 'OpenAI search crawler',
    respectsRobotsTxt: true,
    color: '#10A37F',
  },

  // Anthropic
  ClaudeBot: {
    identifier: 'ClaudeBot',
    name: 'ClaudeBot',
    company: 'Anthropic',
    category: 'training',
    description: 'ClaudeBot collects publicly available data to improve Claude\'s capabilities. Anthropic respects robots.txt and content creator preferences.',
    shortDescription: 'Trains Claude models',
    respectsRobotsTxt: true,
    color: '#D97706',
  },
  'Claude-Web': {
    identifier: 'Claude-Web',
    name: 'Claude-Web',
    company: 'Anthropic',
    category: 'assistant',
    description: 'Claude-Web fetches real-time web content when Claude users need current information during conversations.',
    shortDescription: 'Real-time browsing for Claude',
    respectsRobotsTxt: true,
    color: '#D97706',
  },
  'Claude-SearchBot': {
    identifier: 'Claude-SearchBot',
    name: 'Claude-SearchBot',
    company: 'Anthropic',
    category: 'search',
    description: 'Claude-SearchBot indexes content for Claude\'s search and retrieval capabilities.',
    shortDescription: 'Claude search crawler',
    respectsRobotsTxt: true,
    color: '#D97706',
  },
  'anthropic-ai': {
    identifier: 'anthropic-ai',
    name: 'anthropic-ai',
    company: 'Anthropic',
    category: 'training',
    description: 'Generic Anthropic AI crawler for data collection and model improvement.',
    shortDescription: 'Anthropic data collection',
    respectsRobotsTxt: true,
    color: '#D97706',
  },

  // Perplexity
  PerplexityBot: {
    identifier: 'PerplexityBot',
    name: 'PerplexityBot',
    company: 'Perplexity',
    category: 'search',
    description: 'PerplexityBot powers Perplexity AI\'s search engine, fetching and indexing content to provide AI-generated answers with citations.',
    shortDescription: 'Perplexity search engine',
    respectsRobotsTxt: true,
    color: '#20808D',
  },
  'Perplexity-User': {
    identifier: 'Perplexity-User',
    name: 'Perplexity-User',
    company: 'Perplexity',
    category: 'assistant',
    description: 'Perplexity-User fetches real-time information when users ask questions on Perplexity.',
    shortDescription: 'Perplexity user browsing',
    respectsRobotsTxt: true,
    color: '#20808D',
  },

  // Google
  'Google-Extended': {
    identifier: 'Google-Extended',
    name: 'Google-Extended',
    company: 'Google',
    category: 'training',
    description: 'Google-Extended crawls content for training Bard/Gemini and improving Google\'s AI products. Separate from regular Googlebot.',
    shortDescription: 'Trains Gemini/Bard',
    respectsRobotsTxt: true,
    color: '#4285F4',
  },
  'GoogleCloud-VertexBot': {
    identifier: 'GoogleCloud-VertexBot',
    name: 'Vertex AI Bot',
    company: 'Google',
    category: 'training',
    description: 'Google Cloud Vertex AI bot collects data for enterprise AI model training and improvement.',
    shortDescription: 'Vertex AI training',
    respectsRobotsTxt: true,
    color: '#4285F4',
  },
  Gemini: {
    identifier: 'Gemini',
    name: 'Gemini',
    company: 'Google',
    category: 'assistant',
    description: 'Gemini crawler fetches real-time web content for Google\'s Gemini AI assistant.',
    shortDescription: 'Gemini browsing',
    respectsRobotsTxt: true,
    color: '#4285F4',
  },

  // Meta
  'Meta-ExternalAgent': {
    identifier: 'Meta-ExternalAgent',
    name: 'Meta-ExternalAgent',
    company: 'Meta',
    category: 'training',
    description: 'Meta-ExternalAgent crawls content to train Meta AI, LLaMA, and other Meta AI products.',
    shortDescription: 'Trains Meta AI/LLaMA',
    respectsRobotsTxt: true,
    color: '#0668E1',
  },
  'Meta-ExternalFetcher': {
    identifier: 'Meta-ExternalFetcher',
    name: 'Meta-ExternalFetcher',
    company: 'Meta',
    category: 'assistant',
    description: 'Meta-ExternalFetcher retrieves web content for Meta AI\'s real-time capabilities.',
    shortDescription: 'Meta AI browsing',
    respectsRobotsTxt: true,
    color: '#0668E1',
  },

  // Amazon
  Amazonbot: {
    identifier: 'Amazonbot',
    name: 'Amazonbot',
    company: 'Amazon',
    category: 'training',
    description: 'Amazonbot crawls content to improve Alexa answers, Amazon search, and other Amazon AI services.',
    shortDescription: 'Trains Alexa & Amazon AI',
    respectsRobotsTxt: true,
    color: '#FF9900',
  },

  // ByteDance
  Bytespider: {
    identifier: 'Bytespider',
    name: 'Bytespider',
    company: 'ByteDance',
    category: 'training',
    description: 'Bytespider crawls content for TikTok, Douyin, and ByteDance\'s AI products including their search engines.',
    shortDescription: 'TikTok/ByteDance AI',
    respectsRobotsTxt: true,
    color: '#000000',
  },

  // Apple
  'Applebot-Extended': {
    identifier: 'Applebot-Extended',
    name: 'Applebot-Extended',
    company: 'Apple',
    category: 'training',
    description: 'Applebot-Extended collects data for Siri, Apple Intelligence, and Apple\'s AI features.',
    shortDescription: 'Trains Siri & Apple AI',
    respectsRobotsTxt: true,
    color: '#000000',
  },

  // DuckDuckGo
  DuckAssistBot: {
    identifier: 'DuckAssistBot',
    name: 'DuckAssistBot',
    company: 'DuckDuckGo',
    category: 'search',
    description: 'DuckAssistBot powers DuckDuckGo\'s DuckAssist AI feature, providing AI-generated summaries in search results.',
    shortDescription: 'DuckDuckGo AI search',
    respectsRobotsTxt: true,
    color: '#DE5833',
  },

  // Cohere
  'cohere-ai': {
    identifier: 'cohere-ai',
    name: 'Cohere AI',
    company: 'Cohere',
    category: 'training',
    description: 'Cohere\'s web crawler collects data to train enterprise-focused language models.',
    shortDescription: 'Cohere model training',
    respectsRobotsTxt: true,
    color: '#6366F1',
  },

  // Common Crawl
  CCBot: {
    identifier: 'CCBot',
    name: 'CCBot',
    company: 'Common Crawl',
    category: 'training',
    description: 'CCBot is Common Crawl\'s web crawler. Many AI companies use Common Crawl data for model training, including OpenAI and Anthropic.',
    shortDescription: 'Open web archive (used by many)',
    respectsRobotsTxt: true,
    color: '#6B7280',
  },

  // AI2
  AI2Bot: {
    identifier: 'AI2Bot',
    name: 'AI2Bot',
    company: 'Allen Institute for AI',
    category: 'training',
    description: 'AI2Bot crawls content for the Allen Institute for AI\'s research and open-source AI models.',
    shortDescription: 'AI2 research models',
    respectsRobotsTxt: true,
    color: '#2563EB',
  },

  // DeepSeek
  DeepSeekBot: {
    identifier: 'DeepSeekBot',
    name: 'DeepSeekBot',
    company: 'DeepSeek',
    category: 'training',
    description: 'DeepSeekBot crawls content to train DeepSeek\'s AI models and coding assistants.',
    shortDescription: 'DeepSeek AI training',
    respectsRobotsTxt: true,
    color: '#4F46E5',
  },

  // Mistral
  MistralAI: {
    identifier: 'MistralAI',
    name: 'MistralAI',
    company: 'Mistral AI',
    category: 'training',
    description: 'Mistral AI\'s crawler collects data to train their open-weight language models.',
    shortDescription: 'Mistral model training',
    respectsRobotsTxt: true,
    color: '#F97316',
  },
};

/**
 * Get a bot profile by identifier, with fallback for unknown bots
 */
export function getBotProfile(identifier: string): BotProfile {
  return BOT_PROFILES[identifier] || {
    identifier,
    name: identifier,
    company: 'Unknown',
    category: 'training' as const,
    description: `${identifier} is an AI crawler. We're still gathering information about this bot.`,
    shortDescription: 'AI crawler',
    respectsRobotsTxt: true,
    color: '#6B7280',
  };
}

/**
 * Featured bots for the preview animation (most recognizable)
 */
export const FEATURED_BOTS = [
  'GPTBot',
  'ClaudeBot',
  'PerplexityBot',
  'Google-Extended',
  'Meta-ExternalAgent',
  'Amazonbot',
] as const;

/**
 * Get category styling
 */
export function getCategoryStyle(category: 'training' | 'assistant' | 'search') {
  return BOT_CATEGORIES[category];
}
